import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../config/axios';

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');

  const FHOLogo = ({ size = 80 }) => (
    <svg width={size} height={size} viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`blueGrad-${size}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{stopColor:'#6BB6FF', stopOpacity:1}} />
          <stop offset="100%" style={{stopColor:'#2E7BC6', stopOpacity:1}} />
        </linearGradient>
        <linearGradient id={`redGrad-${size}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{stopColor:'#FF6B58', stopOpacity:1}} />
          <stop offset="100%" style={{stopColor:'#D93025', stopOpacity:1}} />
        </linearGradient>
        <linearGradient id={`greenGrad-${size}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{stopColor:'#5FD88F', stopOpacity:1}} />
          <stop offset="100%" style={{stopColor:'#2EAB5F', stopOpacity:1}} />
        </linearGradient>
      </defs>
      <circle cx="60" cy="62" r="32" fill="none" stroke="#FFD700" strokeWidth="1.5" opacity="0.6"/>
      <circle cx="60" cy="62" r="26" fill="none" stroke="#FFD700" strokeWidth="1.5" opacity="0.7"/>
      <circle cx="60" cy="33" r="8" fill={`url(#blueGrad-${size})`}/>
      <circle cx="34" cy="78" r="8" fill={`url(#redGrad-${size})`}/>
      <circle cx="86" cy="78" r="8" fill={`url(#greenGrad-${size})`}/>
      <text x="60" y="67" fontFamily="'Helvetica Neue', 'Arial', sans-serif" fontSize="16" fontWeight="700" fill="#2C3E50" textAnchor="middle" letterSpacing="1.5">FHO</text>
    </svg>
  );

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await axios.get(`/auth/verify-email/${token}`);
        if (response.data.already_verified) {
          setStatus('already_verified');
          setMessage('Your email was already verified.');
        } else {
          setStatus('success');
          setMessage(response.data.message || 'Email verified successfully!');
        }
        setTimeout(() => navigate('/login', { state: { message: 'Email verified! Please sign in.' } }), 3000);
      } catch (error) {
        setStatus('error');
        const errorDetail = error.response?.data?.detail;
        setMessage(typeof errorDetail === 'string' ? errorDetail : 'Verification failed. The link may be invalid or expired.');
      }
    };
    if (token) { verifyEmail(); } else { setStatus('error'); setMessage('No verification token provided.'); }
  }, [token, navigate]);

  return (
    <div style={{minHeight: '100vh', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif"}}>
      <div style={{width: '100%', maxWidth: '440px', background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '48px', border: '1px solid rgba(255, 255, 255, 0.1)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)', textAlign: 'center'}}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px', cursor: 'pointer'}} onClick={() => navigate('/')}><FHOLogo size={80} /></div>

        {status === 'verifying' && (
          <>
            <div style={{width: '60px', height: '60px', border: '4px solid rgba(74, 144, 226, 0.3)', borderTopColor: '#4A90E2', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 24px'}} />
            <h1 style={{fontSize: '24px', fontWeight: '700', color: 'white', marginBottom: '12px'}}>Verifying Your Email</h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '15px' }}>Please wait...</p>
          </>
        )}

        {(status === 'success' || status === 'already_verified') && (
          <>
            <div style={{width: '80px', height: '80px', background: 'rgba(80, 200, 120, 0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px'}}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#50C878" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
            <h1 style={{fontSize: '28px', fontWeight: '700', color: 'white', marginBottom: '12px'}}>{status === 'success' ? 'Email Verified! ✨' : 'Already Verified'}</h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '16px', marginBottom: '24px' }}>{message}</p>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', marginBottom: '20px' }}>Redirecting to login...</p>
            <Link to="/login" style={{color: '#4A90E2', textDecoration: 'none', fontSize: '15px', fontWeight: '600'}}>Click here if not redirected</Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{width: '80px', height: '80px', background: 'rgba(231, 76, 60, 0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px'}}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </div>
            <h1 style={{fontSize: '28px', fontWeight: '700', color: 'white', marginBottom: '12px'}}>Verification Failed</h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '16px', marginBottom: '30px' }}>{message}</p>
            <Link to="/login" style={{display: 'inline-block', padding: '14px 32px', background: 'linear-gradient(135deg, #4A90E2, #6CB4EE)', color: 'white', textDecoration: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600'}}>Back to Login</Link>
          </>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default VerifyEmail;
